/**
 * The four US Census Bureau regions. We bucket processors into these regions
 * (rather than their individual state) for peer benchmarking: coarser cohorts
 * have more peers, so far more comparisons clear the anonymization floor and the
 * aggregates are statistically more meaningful.
 *
 * Region/state membership follows the Census Bureau's official definition:
 * https://www2.census.gov/geo/pdfs/maps-data/maps/reference/us_regdiv.pdf
 */
export type CensusRegion = 'Northeast' | 'Midwest' | 'South' | 'West';

export const CENSUS_REGIONS: CensusRegion[] = [
  'Northeast',
  'Midwest',
  'South',
  'West',
];

/** 2-letter US state (and DC) code → its US Census region. */
export const STATE_TO_CENSUS_REGION: Record<string, CensusRegion> = {
  // Northeast
  CT: 'Northeast',
  ME: 'Northeast',
  MA: 'Northeast',
  NH: 'Northeast',
  RI: 'Northeast',
  VT: 'Northeast',
  NJ: 'Northeast',
  NY: 'Northeast',
  PA: 'Northeast',
  // Midwest
  IL: 'Midwest',
  IN: 'Midwest',
  MI: 'Midwest',
  OH: 'Midwest',
  WI: 'Midwest',
  IA: 'Midwest',
  KS: 'Midwest',
  MN: 'Midwest',
  MO: 'Midwest',
  NE: 'Midwest',
  ND: 'Midwest',
  SD: 'Midwest',
  // South
  DE: 'South',
  FL: 'South',
  GA: 'South',
  MD: 'South',
  NC: 'South',
  SC: 'South',
  VA: 'South',
  DC: 'South',
  WV: 'South',
  AL: 'South',
  KY: 'South',
  MS: 'South',
  TN: 'South',
  AR: 'South',
  LA: 'South',
  OK: 'South',
  TX: 'South',
  // West
  AZ: 'West',
  CO: 'West',
  ID: 'West',
  MT: 'West',
  NV: 'West',
  NM: 'West',
  UT: 'West',
  WY: 'West',
  AK: 'West',
  CA: 'West',
  HI: 'West',
  OR: 'West',
  WA: 'West',
};

/**
 * Resolve a 2-letter state code (any case) to its US Census region, or null if
 * the code is missing/unrecognized (e.g. a territory outside the 50 states + DC).
 * @param state
 */
export const censusRegionForState = (
  state?: string | null,
): CensusRegion | null => {
  if (!state) return null;
  return STATE_TO_CENSUS_REGION[state.trim().toUpperCase()] ?? null;
};
